import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import type { JobRow, SystemStatus } from "../types";

export function SystemPage({
  onRefresh,
  isAdmin,
}: {
  onRefresh: () => void;
  isAdmin: boolean;
}) {
  const [status, setStatus] = useState<SystemStatus | null>(null);
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [checking, setChecking] = useState(false);
  const [check, setCheck] = useState<{
    zoom: { ok: boolean; user?: string; error?: string };
    anthropic: { ok: boolean; error?: string };
  } | null>(null);
  const [notice, setNotice] = useState("");

  const load = useCallback(() => {
    api.status().then(setStatus).catch(() => {});
    api.listJobs().then((r) => setJobs(r.jobs)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, [load]);

  const runCheck = async () => {
    setChecking(true);
    setCheck(null);
    try {
      setCheck(await api.checkKeys());
    } finally {
      setChecking(false);
    }
  };

  if (!status) {
    return (
      <div className="card">
        <div className="empty">
          <span className="spinner" /> Читаю состояние…
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Состояние</h1>
          <p>Настройки, ключи и очередь фоновых задач.</p>
        </div>
        <div className="row">
          <button onClick={runCheck} disabled={checking}>
            {checking && <span className="spinner" />} Проверить ключи
          </button>
          <button
            onClick={() =>
              api.sweep().then(() => {
                setNotice("Синхронизация выполнена: встречи из Zoom подтянуты, записи проверены.");
                load();
                onRefresh();
              })
            }
            disabled={!status.zoom.configured}
            title="Подтянуть встречи, созданные в Zoom, и проверить готовые записи"
          >
            Синхронизировать с Zoom
          </button>
        </div>
      </div>

      {notice && <div className="banner blue">{notice}</div>}

      {check && (
        <div className="card">
          <div className="card-head">
            <h3>Результат проверки</h3>
          </div>
          <div className="card-body">
            <ul className="list-clean">
              <li className="row">
                <span className={`badge ${check.zoom.ok ? "green" : "red"}`}>
                  Zoom
                </span>
                <span style={{ flex: 1 }}>
                  {check.zoom.ok
                    ? `связь есть, хост: ${check.zoom.user}`
                    : check.zoom.error}
                </span>
              </li>
              <li className="row">
                <span className={`badge ${check.anthropic.ok ? "green" : "red"}`}>
                  Anthropic
                </span>
                <span style={{ flex: 1 }}>
                  {check.anthropic.ok
                    ? `модель ${status.ai.model} отвечает`
                    : check.anthropic.error}
                </span>
              </li>
            </ul>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-head">
          <h3>Интеграции</h3>
        </div>
        <div className="card-body">
          <Row
            label="Zoom API"
            ok={status.zoom.configured}
            value={
              status.zoom.configured
                ? `Server-to-Server OAuth, хост: ${status.zoom.hostEmail}`
                : "ZOOM_ACCOUNT_ID / ZOOM_CLIENT_ID / ZOOM_CLIENT_SECRET не заданы"
            }
          />
          <Row
            label="Вебхуки Zoom"
            ok={status.zoom.webhookConfigured}
            value={
              status.zoom.webhookConfigured
                ? status.zoom.webhookUrl
                : "ZOOM_WEBHOOK_SECRET_TOKEN не задан — события Zoom приниматься не будут"
            }
          />
          <Row
            label="Anthropic"
            ok={status.ai.configured}
            value={
              status.ai.configured
                ? `${status.ai.model}, effort=${status.ai.effort}`
                : "ANTHROPIC_API_KEY не задан"
            }
          />
          <Row
            label="Автозапись по умолчанию"
            ok={status.autoRecording === "cloud"}
            value={
              status.autoRecording === "cloud"
                ? "в облако Zoom — запись стартует сама, разбор работает"
                : `${status.autoRecording} — автоматический разбор недоступен`
            }
          />
          <Row
            label="Опрос Zoom"
            ok={status.pollIntervalMs > 0}
            value={
              status.pollIntervalMs > 0
                ? `каждые ${Math.round(status.pollIntervalMs / 1000)} с — страховка, если вебхуки не долетают`
                : "выключен (POLL_INTERVAL_MS=0)"
            }
          />
        </div>
      </div>

      <ReminderCard
        status={status}
        isAdmin={isAdmin}
        onSaved={(msg) => {
          setNotice(msg);
          load();
          onRefresh();
        }}
      />

      <EventCard isAdmin={isAdmin} onSaved={setNotice} />

      <DigestCard isAdmin={isAdmin} onSaved={setNotice} />

      <StaleCard isAdmin={isAdmin} onSaved={setNotice} />

      <MailCard status={status} isAdmin={isAdmin} onSaved={setNotice} />

      <div className="card">
        <div className="card-head">
          <h3>Расшифровка</h3>
          <span className="badge">режим: {status.transcribe.mode}</span>
        </div>
        <div className="card-body">
          {status.transcribe.providers.map((p) => (
            <Row key={p.name} label={p.name} ok={p.ready} value={p.reason} />
          ))}
          <div className="muted small" style={{ marginTop: 10 }}>
            В режиме <span className="mono">auto</span> провайдеры пробуются по
            очереди: сначала готовый транскрипт Zoom, затем локальный whisper,
            затем Whisper API.
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>Данные</h3>
        </div>
        <div className="card-body">
          <div className="row">
            {Object.entries(status.counts).map(([k, v]) => (
              <span className="badge" key={k}>
                {LABELS[k] ?? k}: {v}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>Очередь задач</h3>
          <div className="row">
            {Object.entries(status.jobs).map(([k, v]) => (
              <span
                key={k}
                className={`badge ${
                  k === "failed" && v > 0 ? "red" : k === "done" ? "green" : ""
                }`}
              >
                {k}: {v}
              </span>
            ))}
          </div>
        </div>
        {jobs.length === 0 ? (
          <div className="empty small">Задач ещё не было.</div>
        ) : (
          <div className="table-wrap"><table>
            <thead>
              <tr>
                <th>#</th>
                <th>Тип</th>
                <th>Статус</th>
                <th>Попытки</th>
                <th>Обновлено</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.id}>
                  <td className="mono">{j.id}</td>
                  <td>
                    <div>{j.type}</div>
                    {j.last_error && (
                      <div
                        className="mono muted small"
                        style={{ wordBreak: "break-word", maxWidth: 420 }}
                      >
                        {j.last_error}
                      </div>
                    )}
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        j.status === "done"
                          ? "green"
                          : j.status === "failed"
                            ? "red"
                            : j.status === "running"
                              ? "blue"
                              : "amber"
                      }`}
                    >
                      {j.status}
                    </span>
                  </td>
                  <td className="small">
                    {j.attempts}/{j.max_attempts}
                  </td>
                  <td className="small nowrap">
                    {new Date(j.updated_at).toLocaleString("ru-RU")}
                  </td>
                  <td>
                    {(j.status === "failed" || j.status === "done") && (
                      <button
                        className="sm ghost"
                        onClick={() =>
                          api.retryJob(j.id).then(() => {
                            setNotice(`Задача #${j.id} поставлена заново.`);
                            load();
                          })
                        }
                      >
                        Повторить
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </>
  );
}

const LABELS: Record<string, string> = {
  people: "люди",
  meetings: "встречи",
  recordings: "записи",
  transcripts: "расшифровки",
  summaries: "разборы",
};

function Row({
  label,
  ok,
  value,
}: {
  label: string;
  ok: boolean;
  value: string;
}) {
  return (
    <div
      className="row"
      style={{ padding: "9px 0", borderBottom: "1px solid var(--border)" }}
    >
      <span className={`badge ${ok ? "green" : "amber"}`}>
        <span className="dot" />
      </span>
      <strong style={{ minWidth: 190 }}>{label}</strong>
      <span className="muted small" style={{ flex: 1, wordBreak: "break-word" }}>
        {value}
      </span>
    </div>
  );
}

/**
 * Напоминания в телеграме. Значение общее для всего сервиса и применяется
 * ко всем встречам сразу; менять может только администратор.
 */
function ReminderCard({
  status,
  isAdmin,
  onSaved,
}: {
  status: SystemStatus;
  isAdmin: boolean;
  onSaved: (message: string) => void;
}) {
  const [value, setValue] = useState(String(status.telegram.reminderLeadMinutes));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setValue(String(status.telegram.reminderLeadMinutes));
  }, [status.telegram.reminderLeadMinutes]);

  const dirty = Number(value) !== status.telegram.reminderLeadMinutes;

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await api.setReminderLead(Number(value));
      onSaved(
        `Напоминание теперь приходит за ${res.reminder_lead_minutes} мин до начала. Настройка действует для всех сотрудников и всех будущих встреч.`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <div className="card-head">
        <h3>Напоминания в телеграме</h3>
        {status.telegram.botUsername && (
          <a
            className="badge blue"
            href={`https://t.me/${status.telegram.botUsername}`}
            target="_blank"
            rel="noreferrer"
          >
            @{status.telegram.botUsername}
          </a>
        )}
      </div>
      <div className="card-body">
        <Row
          label="Бот"
          ok={status.telegram.configured}
          value={
            status.telegram.configured
              ? `подключён, привязали телеграм: ${status.telegram.linkedUsers} чел.`
              : "TELEGRAM_BOT_TOKEN не задан в .env"
          }
        />

        <div className="field" style={{ marginTop: 16, maxWidth: 420 }}>
          <label htmlFor="lead">За сколько минут до начала напоминать</label>
          <div className="row" style={{ flexWrap: "nowrap" }}>
            <input
              id="lead"
              type="number"
              min={1}
              max={1440}
              value={value}
              disabled={!isAdmin}
              onChange={(e) => setValue(e.target.value)}
            />
            {isAdmin && (
              <button
                className="primary nowrap"
                onClick={save}
                disabled={saving || !dirty}
              >
                {saving && <span className="spinner" />} Сохранить
              </button>
            )}
          </div>
          <div className="muted small" style={{ marginTop: 6 }}>
            {isAdmin
              ? "Одна настройка на весь сервис: действует для всех сотрудников и применяется к уже назначенным встречам сразу после сохранения."
              : "Изменить может только администратор."}
          </div>
          {error && <div className="error-text">{error}</div>}
        </div>

        <div className="muted small">
          Бот пишет приглашённым участникам, у которых есть учётная запись
          с привязанным телеграмом. Кроме напоминания он сообщает о переносе
          и отмене встречи.
        </div>
      </div>
    </div>
  );
}

/**
 * Почта для писем сотрудникам. Настраивается в .env на сервере — здесь только
 * видно, работает ли она, и можно отправить себе проверочное письмо.
 */
function MailCard({
  status,
  isAdmin,
  onSaved,
}: {
  status: SystemStatus;
  isAdmin: boolean;
  onSaved: (message: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const test = async () => {
    setBusy(true);
    setError("");
    try {
      const r = await api.testMail();
      if (r.ok) onSaved(`Почта работает: ${r.reason}. Проверьте входящие.`);
      else setError(r.reason);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="card-head">
        <h3>Почта сотрудникам</h3>
      </div>
      <div className="card-body">
        <Row
          label="Отправка"
          ok={status.mail.configured}
          value={
            status.mail.configured
              ? `${status.mail.host}, от имени ${status.mail.from}`
              : "не настроена — SMTP_HOST, SMTP_USER и SMTP_PASS в .env на сервере"
          }
        />

        <p className="muted small">
          Когда почта настроена, при заведении сотрудника ему уходит письмо
          с адресом сервиса, логином и временным паролем. Без неё доступ
          придётся передавать самим — сервис покажет его на экране.
        </p>

        {isAdmin && (
          <button onClick={test} disabled={busy || !status.mail.configured}>
            {busy && <span className="spinner" />} Отправить себе проверочное письмо
          </button>
        )}
        {error && <div className="error-text">{error}</div>}
      </div>
    </div>
  );
}

/**
 * Залежавшиеся клиенты: сколько дней тишины считать тревожным и сводка
 * администраторам по понедельникам.
 */
function StaleCard({
  isAdmin,
  onSaved,
}: {
  isAdmin: boolean;
  onSaved: (message: string) => void;
}) {
  const [days, setDays] = useState("7");
  const [saved, setSaved] = useState(7);
  const [weekly, setWeekly] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .getSettings()
      .then((s) => {
        setDays(String(s.stale_days));
        setSaved(s.stale_days);
        setWeekly(s.stale_weekly_enabled);
      })
      .catch(() => {});
  }, []);

  const apply = async (patch: { stale_days?: number; stale_weekly_enabled?: boolean }) => {
    setBusy(true);
    setError("");
    try {
      const r = await api.setStale(patch);
      setDays(String(r.stale_days));
      setSaved(r.stale_days);
      setWeekly(r.stale_weekly_enabled);
      onSaved(
        patch.stale_days !== undefined
          ? `Клиент считается залежавшимся после ${r.stale_days} дн. без касаний.`
          : r.stale_weekly_enabled
            ? "Сводка по залежавшимся будет приходить по понедельникам."
            : "Сводка по залежавшимся выключена.",
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    setError("");
    try {
      const r = await api.sendStaleNow();
      onSaved(
        r.stale === 0
          ? "Залежавшихся клиентов нет — отправлять нечего."
          : `Сводка отправлена: ${r.stale} клиентов. Загляните в телеграм.`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="card-head">
        <h3>Залежавшиеся клиенты</h3>
        <span className={`badge ${weekly ? "green" : ""}`}>
          {weekly ? "сводка по понедельникам" : "сводка выключена"}
        </span>
      </div>
      <div className="card-body">
        <p className="muted small" style={{ marginTop: 0 }}>
          Клиент залежался, если с ним дольше порога не было ни звонка, ни письма,
          ни встречи, ни заметки, ни закрытой задачи. Такие подсвечиваются в списке
          лидов и на воронке, а по понедельникам администратор получает сводку
          по всей команде — во время утренней сводки.
        </p>

        <div className="field" style={{ maxWidth: 420 }}>
          <label htmlFor="stale-days">Сколько дней тишины — уже тревожно</label>
          <div className="row" style={{ flexWrap: "nowrap" }}>
            <input
              id="stale-days"
              type="number"
              min={1}
              max={180}
              value={days}
              disabled={!isAdmin}
              onChange={(e) => setDays(e.target.value)}
            />
            {isAdmin && (
              <button
                className="primary nowrap"
                onClick={() => apply({ stale_days: Number(days) })}
                disabled={busy || Number(days) === saved}
              >
                Сохранить
              </button>
            )}
          </div>
          {error && <div className="error-text">{error}</div>}
        </div>

        {isAdmin && (
          <div className="row">
            <button onClick={() => apply({ stale_weekly_enabled: !weekly })} disabled={busy}>
              {weekly ? "Выключить сводку" : "Включить сводку"}
            </button>
            <button onClick={test} disabled={busy}>
              {busy && <span className="spinner" />} Прислать сейчас
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Утренняя сводка по лидам: кому сегодня звонить, кто просрочен, что появилось
 * вчера. Приходит раз в сутки в телеграм каждому, у кого есть что разбирать.
 */
function DigestCard({
  isAdmin,
  onSaved,
}: {
  isAdmin: boolean;
  onSaved: (message: string) => void;
}) {
  const [time, setTime] = useState("09:00");
  const [enabled, setEnabled] = useState(true);
  const [zone, setZone] = useState("");
  const [saved, setSaved] = useState("09:00");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .getSettings()
      .then((s) => {
        setTime(s.digest_time);
        setSaved(s.digest_time);
        setEnabled(s.digest_enabled);
        setZone(s.timezone);
      })
      .catch(() => {});
  }, []);

  const apply = async (patch: { digest_time?: string; digest_enabled?: boolean }) => {
    setBusy(true);
    setError("");
    try {
      const res = await api.setDigest(patch);
      setTime(res.digest_time);
      setSaved(res.digest_time);
      setEnabled(res.digest_enabled);
      onSaved(
        res.digest_enabled
          ? `Сводка будет приходить в ${res.digest_time}.`
          : "Утренняя сводка выключена.",
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    setError("");
    try {
      const r = await api.sendDigestNow();
      onSaved(
        r.sent > 0
          ? `Сводка отправлена: ${r.sent}. Загляните в телеграм.`
          : "Отправлять нечего: ни у кого нет просроченных, сегодняшних или вчерашних лидов.",
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="card-head">
        <h3>Утренняя сводка</h3>
        <span className={`badge ${enabled ? "green" : ""}`}>
          {enabled ? "включена" : "выключена"}
        </span>
      </div>
      <div className="card-body">
        <p className="muted small" style={{ marginTop: 0 }}>
          Раз в сутки бот пишет каждому менеджеру, с кем сегодня связаться, кто
          просрочен и что появилось вчера. Если разбирать нечего — сообщение
          не приходит: пустая сводка каждое утро быстро приучает её не читать.
        </p>

        <div className="field" style={{ maxWidth: 420 }}>
          <label htmlFor="digest">Время рассылки{zone ? ` (${zone})` : ""}</label>
          <div className="row" style={{ flexWrap: "nowrap" }}>
            <input
              id="digest"
              type="time"
              value={time}
              disabled={!isAdmin || !enabled}
              onChange={(e) => setTime(e.target.value)}
            />
            {isAdmin && (
              <button
                className="primary nowrap"
                onClick={() => apply({ digest_time: time })}
                disabled={busy || !enabled || time === saved}
              >
                Сохранить
              </button>
            )}
          </div>
          {error && <div className="error-text">{error}</div>}
        </div>

        {isAdmin && (
          <div className="row">
            <button onClick={() => apply({ digest_enabled: !enabled })} disabled={busy}>
              {enabled ? "Выключить сводку" : "Включить сводку"}
            </button>
            <button onClick={test} disabled={busy || !enabled}>
              {busy && <span className="spinner" />} Прислать сейчас
            </button>
          </div>
        )}

        <div className="muted small" style={{ marginTop: 10 }}>
          Администратор получает ещё и итог по всей команде.
        </div>
      </div>
    </div>
  );
}

/**
 * Текущая выставка. Пока она указана, каждая новая карточка лида помечается
 * ею автоматически — потом можно посмотреть «всё, что привезли с Интершарма».
 */
function EventCard({
  isAdmin,
  onSaved,
}: {
  isAdmin: boolean;
  onSaved: (message: string) => void;
}) {
  const [value, setValue] = useState("");
  const [saved, setSaved] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .getSettings()
      .then((s) => {
        setValue(s.current_event);
        setSaved(s.current_event);
      })
      .catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await api.setCurrentEvent(value);
      setSaved(res.current_event);
      onSaved(
        res.current_event
          ? `Текущая выставка: «${res.current_event}». Новые лиды помечаются ею.`
          : "Выставка снята — новые лиды больше не помечаются.",
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="card-head">
        <h3>Текущая выставка</h3>
        {saved && <span className="badge lime">{saved}</span>}
      </div>
      <div className="card-body">
        <div className="field" style={{ maxWidth: 460 }}>
          <label htmlFor="event">Название мероприятия</label>
          <div className="row" style={{ flexWrap: "nowrap" }}>
            <input
              id="event"
              value={value}
              disabled={!isAdmin}
              placeholder="Интершарм 2026"
              onChange={(e) => setValue(e.target.value)}
            />
            {isAdmin && (
              <button
                className="primary nowrap"
                onClick={save}
                disabled={busy || value === saved}
              >
                {busy && <span className="spinner" />} Сохранить
              </button>
            )}
          </div>
          <div className="muted small" style={{ marginTop: 6 }}>
            {isAdmin
              ? "Пока поле заполнено, все новые карточки лидов помечаются этой выставкой. После мероприятия очистите поле."
              : "Изменить может только администратор."}
          </div>
          {error && <div className="error-text">{error}</div>}
        </div>
      </div>
    </div>
  );
}
